import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Typography,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import paybosService from '../services/paybosService';
import { PaybosConfig, PaybosConfigInput } from '../types/paybos';

const PaybosSettings: React.FC = () => {
  const [config, setConfig] = useState<PaybosConfig | null>(null);
  const [formData, setFormData] = useState<PaybosConfigInput>({
    api_key: '',
    webhook_secret: '',
    is_production: false,
    callback_url: '',
    return_url: '',
    expired_in: 3600,
    service_fee_fixed: 0,
    service_fee_percent: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [gatewayEnabled, setGatewayEnabled] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await paybosService.config.getConfig();
      if (response.success && response.data) {
        setConfig(response.data);
        setGatewayEnabled(response.data.is_enabled !== false);
        setFormData((prev) => ({
          ...prev,
          is_production: response.data.is_production,
          callback_url: response.data.callback_url || '',
          return_url: response.data.return_url || '',
          expired_in: response.data.expired_in || 3600,
          service_fee_fixed: Number(response.data.service_fee_fixed || 0),
          service_fee_percent: Number(response.data.service_fee_percent || 0),
        }));
      }
    } catch (err: any) {
      setError(err.message || 'Gagal memuat konfigurasi PayBOS');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!config) {
      setError('Simpan konfigurasi PayBOS terlebih dahulu sebelum mengaktifkan gateway');
      return;
    }
    try {
      setToggling(true);
      setError('');
      const response = await paybosService.config.toggleEnabled(enabled);
      if (response.success) {
        setGatewayEnabled(enabled);
        setConfig((prev) => (prev ? { ...prev, is_enabled: enabled } : prev));
        setSuccess(response.message || (enabled ? 'PayBOS diaktifkan' : 'PayBOS dinonaktifkan'));
      } else {
        setError(response.message || 'Gagal mengubah status gateway');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal mengubah status gateway');
    } finally {
      setToggling(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!config && (!formData.api_key || !formData.webhook_secret)) {
      setError('API Key dan Webhook Secret wajib diisi untuk konfigurasi pertama');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await paybosService.config.updateConfig(formData);
      if (response.success) {
        setSuccess('Konfigurasi PayBOS berhasil disimpan');
        setConfig(response.data);
        setFormData((prev) => ({ ...prev, api_key: '', webhook_secret: '' }));
      } else {
        setError(response.message || 'Gagal menyimpan konfigurasi');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  const frontendBase = window.location.origin;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title="PayBOS Payment Gateway"
        subheader="Integrasi QRIS via PayBOS — dokumentasi: app.paybos.id/docs/api"
        action={
          config?.is_active ? (
            <Chip label={config.is_production ? 'Production' : 'Sandbox'} color="primary" size="small" />
          ) : (
            <Chip label="Belum dikonfigurasi" color="warning" size="small" />
          )
        }
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: gatewayEnabled ? 'success.light' : 'divider',
            bgcolor: gatewayEnabled ? 'success.50' : 'grey.50',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Status Gateway PayBOS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {config
                  ? gatewayEnabled
                    ? 'Aktif — pembayaran QRIS PayBOS dapat dibuat'
                    : 'Nonaktif — pembayaran PayBOS tidak tersedia'
                  : 'Belum dikonfigurasi — simpan konfigurasi dulu'}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={gatewayEnabled ? 'AKTIF' : 'NONAKTIF'}
                color={gatewayEnabled ? 'success' : 'default'}
                size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={gatewayEnabled}
                    onChange={(e) => handleToggleEnabled(e.target.checked)}
                    disabled={!config || toggling}
                    color="success"
                  />
                }
                label={gatewayEnabled ? 'Aktif' : 'Nonaktif'}
              />
            </Stack>
          </Stack>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Callback URL (webhook): <code>{apiBase}/paybos/callback</code>
          <br />
          Return URL (disarankan): <code>{frontendBase}/payment/paybos/success</code>
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="API Key"
              placeholder="pk_test_... atau pk_live_..."
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              fullWidth
              helperText={config ? 'Kosongkan jika tidak ingin mengubah API key' : 'Dari dashboard PayBOS → API Key'}
            />
            <TextField
              label="Webhook Secret"
              type="password"
              value={formData.webhook_secret}
              onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
              fullWidth
              helperText="Dari dashboard PayBOS → Pengaturan → Webhook Secret"
            />
            <TextField
              label="Callback URL"
              value={formData.callback_url}
              onChange={(e) => setFormData({ ...formData, callback_url: e.target.value })}
              fullWidth
              placeholder={`${apiBase}/paybos/callback`}
            />
            <TextField
              label="Return URL"
              value={formData.return_url}
              onChange={(e) => setFormData({ ...formData, return_url: e.target.value })}
              fullWidth
              placeholder={`${frontendBase}/payment/paybos/success`}
            />
            <Typography variant="subtitle2" fontWeight={600}>
              Biaya layanan (dibebankan ke pelanggan)
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Biaya tetap (Rp)"
                type="number"
                value={formData.service_fee_fixed ?? 0}
                onChange={(e) =>
                  setFormData({ ...formData, service_fee_fixed: parseFloat(e.target.value) || 0 })
                }
                fullWidth
                inputProps={{ min: 0, step: 500 }}
                helperText="Contoh: 2000 = Rp 2.000 per transaksi"
              />
              <TextField
                label="Biaya persen (%)"
                type="number"
                value={formData.service_fee_percent ?? 0}
                onChange={(e) =>
                  setFormData({ ...formData, service_fee_percent: parseFloat(e.target.value) || 0 })
                }
                fullWidth
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Dihitung dari nominal tagihan"
              />
            </Stack>
            <Alert severity="info" variant="outlined">
              Total QRIS = tagihan + biaya tetap + (tagihan × persen). Keduanya bisa dikombinasikan.
            </Alert>
            <TextField
              label="Expired In (detik)"
              type="number"
              value={formData.expired_in}
              onChange={(e) => setFormData({ ...formData, expired_in: parseInt(e.target.value, 10) || 3600 })}
              fullWidth
              inputProps={{ min: 300 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_production}
                  onChange={(e) => setFormData({ ...formData, is_production: e.target.checked })}
                />
              }
              label="Mode Production (pk_live_)"
            />
            <Stack direction="row" spacing={2}>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan Konfigurasi'}
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadConfig}>
                Muat Ulang
              </Button>
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaybosSettings;
